'use client'

import { useCallback } from 'react'

import { useReCaptcha } from '@circlefin/recaptcha/react'
import { useAugmentedReCaptcha } from '@features/common.hooks/useAugmentedReCaptcha'
import { RequestFailed } from '@features/faucet.components/RequestFailed'
import { RequestLimited } from '@features/faucet.components/RequestLimited'
import { RequestSuccessful } from '@features/faucet.components/RequestSuccessful'
import {
  FaucetConfigurationDocument,
  RequestTokenDocument,
} from '@features/faucet.graphql'
import { useFeatureFlagV2 } from '@features/flags.hooks/useFeatureFlag'
import { useMutation, useQuery } from '@shared/apollo'
import { getErrorCode, RECAPTCHA_ERROR } from '@shared/apollo/lib/error/codes'

import { RequestTokenForm } from '../RequestTokenForm/RequestTokenForm'

import type { OnReCaptchaCompletedProps } from '@features/common.hooks/useAugmentedReCaptcha'
import type { RequestTokenInput } from '@features/faucet.graphql'
import type { FaucetEventOverrideAmounts } from '@features/faucet.graphql/datasources/FaucetApi/eventOverrides'

const RECAPTCHA_ACTION = 'request_token'

interface RequestTokenProps {
  eventId?: string
  eventOverrideAmounts?: FaucetEventOverrideAmounts
}

export const RequestToken = ({
  eventId,
  eventOverrideAmounts,
}: RequestTokenProps) => {
  const { data, error, loading, refetch } = useQuery(
    FaucetConfigurationDocument,
    { pollInterval: 60_000 },
  )

  const [
    requestToken,
    {
      data: requestTokenData,
      reset: requestTokenReset,
      error: requestTokenError,
    },
  ] = useMutation(RequestTokenDocument, { onError: () => {} })

  const [recaptchaV2FallbackEnabled] = useFeatureFlagV2(
    'recaptchaV2FallbackEnabled',
    false,
  )

  /**
   * Start: ReCaptcha V3 logic (no V2 fallback) used when recaptchaV2FallbackEnabled is false.
   */
  const { withReCaptcha } = useReCaptcha({
    action: RECAPTCHA_ACTION,
  })

  const onSubmit = useCallback(
    async (input: RequestTokenInput) => {
      await withReCaptcha({
        onCompleted: async (context) => {
          await requestToken({
            variables: {
              input: eventId ? { ...input, eventId } : input,
            },
            context,
          })
        },
        onError: () => {},
      })
    },
    [eventId, requestToken, withReCaptcha],
  )
  /**
   * End: ReCaptcha V3 logic (no V2 fallback) used when recaptchaV2FallbackEnabled is false.
   */

  /**
   * Start: ReCaptcha V3 logic with V2 fallback used when recaptchaV2FallbackEnabled is true.
   */
  const handleReCaptchaCompleted = useCallback(
    async ({
      values,
      context,
      onError,
    }: OnReCaptchaCompletedProps<RequestTokenInput>) => {
      await requestToken({
        variables: {
          input: eventId ? { ...values, eventId } : values,
        },
        context,
        onError,
      })
    },
    [requestToken, eventId],
  )

  const [handleSubmit, { v2 }] = useAugmentedReCaptcha({
    action: RECAPTCHA_ACTION,
    onReCaptchaCompleted: handleReCaptchaCompleted,
  })
  /**
   * End: ReCaptcha V3 logic with V2 fallback used when recaptchaV2FallbackEnabled is true.
   */

  const submissionHandler = recaptchaV2FallbackEnabled ? handleSubmit : onSubmit

  const refetchConfiguration = useCallback(() => {
    void refetch()
  }, [refetch])

  if (error) {
    // show error screen for initial load if the configuration could not be loaded
    return <RequestFailed onReset={refetchConfiguration} />
  }

  const isRequestTokenError = Boolean(
    requestTokenError || requestTokenData?.requestToken.status === 'error',
  )

  // Recaptcha error is handled in the useAugmentedReCaptcha hook and RequestTokenForm component.
  if (
    isRequestTokenError &&
    getErrorCode(requestTokenError?.graphQLErrors.at(0)) !== RECAPTCHA_ERROR
  ) {
    return <RequestFailed onReset={requestTokenReset} />
  }

  if (requestTokenData?.requestToken.status === 'rate_limited') {
    return <RequestLimited onReset={requestTokenReset} />
  }

  if (requestTokenData?.requestToken.status === 'success') {
    return (
      <RequestSuccessful
        data={requestTokenData.requestToken}
        onReset={requestTokenReset}
      />
    )
  }

  return (
    <RequestTokenForm
      data={data?.faucetConfiguration}
      eventOverrideAmounts={eventOverrideAmounts}
      loading={loading}
      onSubmit={submissionHandler}
      recaptchaV2={v2}
    />
  )
}
